import React, { useState, useEffect, useCallback } from 'react';
import {
  BoulderCompetitionData,
  BoulderAthlete,
  BoulderClass,
  BoulderSportClass,
  Boulder,
  BoulderResult,
  generateId,
  getResult,
  setResult,
  mergeResult,
  scoreBoulder,
  formatScore,
  computeClassRankings,
} from '../utils/boulderCompetition';
import { loadBoulderData, saveBoulderData, saveResultEntry } from '../firebase/boulderService';
import { useCompetition, AdminAccessGate } from '../context/CompetitionContext';

// ─── JuryCell ────────────────────────────────────────────────────────────────

interface JuryCellProps {
  result: BoulderResult | undefined;
  onChange: (patch: Partial<Pick<BoulderResult, 'attempts' | 'zoned' | 'topped'>>) => void;
}

const JuryCell: React.FC<JuryCellProps> = ({ result, onChange }) => {
  const attempts = result?.attempts ?? 0;
  const zoned   = result?.zoned   ?? false;
  const topped  = result?.topped  ?? false;
  const score   = result ? scoreBoulder(result) : 0;
  const active  = zoned || topped || attempts > 0;

  return (
    <div className={`flex flex-col items-center gap-0.5 px-1 py-1.5 rounded ${active ? 'bg-blue-50' : ''}`}>
      <input
        type="number"
        min={0}
        max={99}
        value={attempts === 0 ? '' : attempts}
        onChange={e => onChange({ attempts: Math.max(0, parseInt(e.target.value, 10) || 0) })}
        className="w-10 text-center border border-gray-300 rounded text-sm px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-secondary"
        placeholder="—"
      />
      <div className="flex gap-2 text-xs mt-0.5">
        <label className="flex items-center gap-0.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={zoned}
            onChange={e => {
              const checked = e.target.checked;
              onChange({ zoned: checked, topped: checked ? topped : false });
            }}
            className="accent-blue-500"
          />
          <span className={zoned ? 'font-bold text-blue-600' : 'text-gray-400'}>Z</span>
        </label>
        <label className="flex items-center gap-0.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={topped}
            onChange={e => {
              const checked = e.target.checked;
              onChange({ topped: checked, zoned: checked ? true : zoned });
            }}
            className="accent-green-600"
          />
          <span className={topped ? 'font-bold text-green-700' : 'text-gray-400'}>T</span>
        </label>
      </div>
      {score > 0 && (
        <span className={`text-xs font-bold ${topped ? 'text-green-700' : 'text-blue-600'}`}>
          {formatScore(score)}
        </span>
      )}
    </div>
  );
};

// ─── BoulderAdminPage ─────────────────────────────────────────────────────────

type AdminTab = 'setup' | 'athletes' | 'jury' | 'rankings';

const BoulderAdminInner: React.FC = () => {
  const { competitionId, metadata } = useCompetition();
  const [data,    setData]    = useState<BoulderCompetitionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('setup');
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [dirty,   setDirty]   = useState(false);

  // Athletes tab
  const [newAthleteName,    setNewAthleteName]    = useState('');
  const [newAthleteGender,  setNewAthleteGender]  = useState<'male' | 'female'>('male');
  const [newAthleteClassId, setNewAthleteClassId] = useState('');

  // Setup tab — classes
  const [newSportClassName, setNewSportClassName] = useState('');
  // Setup tab — merging groups
  const [newMenGroupName,   setNewMenGroupName]   = useState('');
  const [newWomenGroupName, setNewWomenGroupName] = useState('');
  // Setup tab — drag and drop
  const [dndClassId,     setDndClassId]     = useState<string | null>(null);
  const [dndFromGroupId, setDndFromGroupId] = useState<string | null>(null);
  const [dndFromGender,  setDndFromGender]  = useState<'men' | 'women' | null>(null);
  const [dndTarget,      setDndTarget]      = useState<string | null>(null);

  useEffect(() => {
    loadBoulderData(competitionId)
      .then(d => {
        setData(d);
        setLoading(false);
        if (d.setup.classes.length > 0) setNewAthleteClassId(d.setup.classes[0].id);
      })
      .catch(err => { setError(String(err)); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  const persist = useCallback((d: BoulderCompetitionData) => {
    setData(d);
    setDirty(true);
    setSaved(false);
  }, []);

  const saveNow = useCallback(() => {
    if (!data) return;
    setSaving(true);
    saveBoulderData(competitionId, data)
      .then(() => { setSaved(true); setDirty(false); setTimeout(() => setSaved(false), 3000); })
      .catch(err => setError(String(err)))
      .finally(() => setSaving(false));
  }, [data]);

  // ── Setup handlers ──────────────────────────────────────────────────────

  const addBoulder = () => {
    if (!data) return;
    const nextNum = data.setup.boulders.length
      ? Math.max(...data.setup.boulders.map(b => b.number)) + 1
      : 1;
    const b: Boulder = { id: generateId(), number: nextNum };
    persist({ ...data, setup: { ...data.setup, boulders: [...data.setup.boulders, b] } });
  };

  const deleteBoulder = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this boulder? All results for it will be lost.')) return;
    const boulders = data.setup.boulders.filter(b => b.id !== id);
    const classes  = data.setup.classes.map(c => ({
      ...c, boulderIds: c.boulderIds.filter(bid => bid !== id),
    }));
    const results  = Object.fromEntries(
      Object.entries(data.results).filter(([, r]) => r.boulderId !== id),
    );
    persist({ ...data, setup: { ...data.setup, boulders, classes }, results });
  };

  const toggleBoulderAssignment = (classId: string, boulderId: string, assigned: boolean) => {
    if (!data) return;
    const classes = data.setup.classes.map(c => {
      if (c.id !== classId) return c;
      const boulderIds = assigned
        ? [...c.boulderIds, boulderId]
        : c.boulderIds.filter(bid => bid !== boulderId);
      return { ...c, boulderIds };
    });
    persist({ ...data, setup: { ...data.setup, classes } });
  };

  const toggleLock = () => {
    if (!data) return;
    persist({ ...data, setup: { ...data.setup, locked: !data.setup.locked } });
  };

  const addSportClass = () => {
    if (!data || !newSportClassName.trim()) return;
    const sc: BoulderSportClass = { id: generateId(), name: newSportClassName.trim() };
    persist({ ...data, setup: { ...data.setup, sportClasses: [...data.setup.sportClasses, sc] } });
    setNewSportClassName('');
  };

  const deleteSportClass = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this class? Athletes assigned to it will be unassigned.')) return;
    persist({
      ...data,
      setup: {
        ...data.setup,
        sportClasses: data.setup.sportClasses.filter(sc => sc.id !== id),
        classes: data.setup.classes.map(c => ({
          ...c, classIds: (c.classIds ?? []).filter(cid => cid !== id),
        })),
      },
      athletes: data.athletes.map(a => a.classId === id ? { ...a, classId: '' } : a),
    });
  };

  const toggleGroupClass = (groupId: string, classId: string, include: boolean) => {
    if (!data) return;
    const className = data.setup.sportClasses.find(sc => sc.id === classId)?.name ?? classId;
    const targetGender = data.setup.classes.find(c => c.id === groupId)?.gender;
    const classes = data.setup.classes.map(c => {
      let classIds = c.classIds ?? [];
      if (c.id === groupId) {
        classIds = include ? [...classIds.filter(id => id !== classId), classId] : classIds.filter(id => id !== classId);
        const name = include && !c.name.trim() ? className : c.name;
        return { ...c, classIds, name };
      } else if (include && c.gender === targetGender) {
        classIds = classIds.filter(id => id !== classId);
      }
      return { ...c, classIds };
    });
    persist({ ...data, setup: { ...data.setup, classes } });
  };

  const addMergingGroup = (gender: 'men' | 'women', name: string) => {
    if (!data || !name.trim()) return;
    const g: BoulderClass = { id: generateId(), name: name.trim(), boulderIds: [], gender };
    persist({ ...data, setup: { ...data.setup, classes: [...data.setup.classes, g] } });
    if (gender === 'men') setNewMenGroupName(''); else setNewWomenGroupName('');
  };

  const deleteMergingGroup = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this merging group? Athletes in it will lose their group assignment.')) return;
    persist({
      ...data,
      setup: { ...data.setup, classes: data.setup.classes.filter(c => c.id !== id) },
      athletes: data.athletes.map(a => a.classId === id ? { ...a, classId: '' } : a),
    });
  };

  const resetAll = () => {
    if (!window.confirm('Reset ALL boulder data? This cannot be undone.')) return;
    persist({ setup: { boulders: [], sportClasses: [], classes: [], locked: false }, athletes: [], results: {} });
  };

  // ── Athletes handlers ────────────────────────────────────────────────────

  const addAthlete = () => {
    if (!data) return;
    const name = newAthleteName.trim();
    if (!name || !newAthleteClassId) return;
    const a: BoulderAthlete = { id: generateId(), name, gender: newAthleteGender, classId: newAthleteClassId };
    persist({ ...data, athletes: [...data.athletes, a] });
    setNewAthleteName('');
  };

  const updateAthleteName = (id: string, name: string) => {
    if (!data) return;
    persist({ ...data, athletes: data.athletes.map(a => a.id === id ? { ...a, name } : a) });
  };

  const updateAthleteClass = (id: string, classId: string) => {
    if (!data) return;
    persist({ ...data, athletes: data.athletes.map(a => a.id === id ? { ...a, classId } : a) });
  };

  const deleteAthlete = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this athlete? Their results will be lost.')) return;
    persist({
      ...data,
      athletes: data.athletes.filter(a => a.id !== id),
      results:  Object.fromEntries(
        Object.entries(data.results).filter(([, r]) => r.athleteId !== id),
      ),
    });
  };

  // ── Jury handler ─────────────────────────────────────────────────────────

  const updateResult = (
    athleteId: string,
    boulderId: string,
    patch: Partial<Pick<BoulderResult, 'attempts' | 'zoned' | 'topped'>>,
  ) => {
    if (!data) return;
    const updated = mergeResult(data.results, athleteId, boulderId, patch);
    // Update local state immediately without marking dirty — saveResultEntry persists atomically
    setData({ ...data, results: setResult(data.results, athleteId, boulderId, patch) });
    // Atomically write only this one result to Firestore — safe for concurrent jury members
    saveResultEntry(competitionId, athleteId, boulderId, updated).catch(err =>
      console.error('Result save error:', err),
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Guards
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Loading competition data…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded max-w-lg">
          <strong>Failed to load data</strong><br />
          <span className="text-sm">{error ?? 'Unknown error'}</span><br />
          <button onClick={() => window.location.reload()} className="mt-3 text-sm underline">Retry</button>
        </div>
      </div>
    );
  }

  const { setup, athletes, results } = data;
  const locked = setup.locked;
  const classRankings = computeClassRankings(data);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="no-print bg-dark text-white py-6 px-4 shadow">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">Boulder Competition — Admin</h1>
            <p className="text-gray-400 text-sm mt-1">
              {metadata?.name ?? competitionId} ·{' '}
              <a href={`/competition/${competitionId}/results/boulder`} target="_blank" rel="noopener noreferrer" className="text-blue-300 underline">
                Open public display ↗
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {dirty  && <span className="text-yellow-300 text-sm">Unsaved changes</span>}
            {saved  && <span className="text-green-300 text-sm">Saved ✓</span>}
            <button
              onClick={saveNow}
              disabled={saving || !dirty}
              className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={resetAll}
              className="px-3 py-1.5 bg-red-700 text-white rounded text-sm hover:bg-red-800"
            >
              Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="no-print border-b bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          {(['setup', 'athletes', 'jury', 'rankings'] as AdminTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-semibold capitalize border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-secondary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-dark'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ══ SETUP TAB ══ */}
        {activeTab === 'setup' && (
          <div>
            {/* Lock banner */}
            <div className={`mb-6 flex items-center justify-between p-4 rounded-lg border-2 ${locked ? 'bg-yellow-50 border-yellow-400' : 'bg-white border-gray-200'}`}>
              <div>
                <p className="font-semibold text-dark">{locked ? '🔒 Setup is locked' : '🔓 Setup is open'}</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {locked ? 'Unlock to modify boulders or boulder assignments.' : 'Lock setup before starting the jury phase.'}
                </p>
              </div>
              <button
                onClick={toggleLock}
                className={`px-4 py-2 rounded font-semibold text-sm ${locked ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-gray-700 hover:bg-gray-800 text-white'}`}
              >
                {locked ? 'Unlock Setup' : 'Lock Setup'}
              </button>
            </div>

            {/* Boulders */}
            <div className="max-w-xs">
              <h2 className="text-xl font-bold text-dark mb-4">Boulders ({setup.boulders.length})</h2>
              <div className="bg-white rounded shadow overflow-hidden mb-4">
                {setup.boulders.length === 0 ? (
                  <p className="p-4 text-gray-400 text-sm italic">No boulders yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {setup.boulders.map(b => (
                        <tr key={b.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono font-bold text-secondary w-16">
                            B{b.number}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => deleteBoulder(b.id)}
                              disabled={locked}
                              className="text-red-400 hover:text-red-600 text-xs disabled:opacity-30"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {!locked && (
                <button
                  onClick={addBoulder}
                  className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800"
                >
                  + Add Boulder
                </button>
              )}
            </div>

            {/* Classes — shared */}
            <div className="mt-8">
              <h2 className="text-xl font-bold text-dark mb-2">Classes ({setup.sportClasses.length})</h2>
              <p className="text-sm text-gray-500 mb-3">Individual paraclimbing classes, shared by men and women. Drag them into the merging groups below.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {setup.sportClasses.length === 0
                  ? <span className="text-sm text-gray-400 italic">No classes yet — add below.</span>
                  : setup.sportClasses.map(sc => (
                      <div key={sc.id} className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded text-xs font-semibold text-dark select-none">
                        {sc.name}
                        {!locked && <button onClick={() => deleteSportClass(sc.id)} className="text-gray-300 hover:text-red-500 ml-1">✕</button>}
                      </div>
                    ))
                }
              </div>
              {!locked && (
                <div className="flex gap-2 max-w-xs">
                  <input type="text" value={newSportClassName}
                    onChange={e => setNewSportClassName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addSportClass()}
                    placeholder="Add class (e.g. RP1, AL2)"
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary" />
                  <button onClick={addSportClass} disabled={!newSportClassName.trim()}
                    className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40">
                    + Add
                  </button>
                </div>
              )}
            </div>

            {/* Merging Groups — Men / Women */}
            <div className="mt-8 grid md:grid-cols-2 gap-8">
              {(['men', 'women'] as const).map(col => {
                const colGroups    = setup.classes.filter(c => c.gender === col);
                const colAssigned  = new Set(colGroups.flatMap(c => c.classIds ?? []));
                const colUnassigned = setup.sportClasses.filter(sc => !colAssigned.has(sc.id));
                const colGroupName    = col === 'men' ? newMenGroupName    : newWomenGroupName;
                const setColGroupName = col === 'men' ? setNewMenGroupName : setNewWomenGroupName;
                return (
                  <div key={col}>
                    <h2 className={`text-xl font-bold mb-2 ${col === 'men' ? 'text-blue-700' : 'text-pink-600'}`}>
                      {col === 'men' ? 'Men' : 'Women'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">Drop classes into groups. Drag back to pool to unassign.</p>
                    {/* Pool */}
                    <div
                      onDragOver={e => { if (dndFromGender === col) { e.preventDefault(); setDndTarget(`pool-${col}`); } }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDndTarget(null); }}
                      onDrop={e => {
                        e.preventDefault();
                        if (dndClassId && dndFromGroupId && dndFromGender === col) toggleGroupClass(dndFromGroupId, dndClassId, false);
                        setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null);
                      }}
                      className={`min-h-14 rounded border-2 border-dashed p-3 mb-4 flex flex-wrap gap-2 content-start transition-colors ${
                        dndTarget === `pool-${col}` ? 'border-secondary bg-blue-50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      {colUnassigned.length === 0
                        ? <span className="text-xs text-gray-400 italic self-center">
                            {setup.sportClasses.length === 0 ? 'Add classes above.' : 'All classes assigned →'}
                          </span>
                        : colUnassigned.map(sc => (
                            <div
                              key={sc.id}
                              draggable={!locked}
                              onDragStart={() => { setDndClassId(sc.id); setDndFromGroupId(null); setDndFromGender(col); }}
                              onDragEnd={() => { setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null); }}
                              className={`flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 rounded text-xs font-semibold select-none transition-opacity ${
                                locked ? 'cursor-default' : 'cursor-grab hover:border-secondary hover:bg-blue-50'
                              } ${dndClassId === sc.id && dndFromGender === col ? 'opacity-40' : ''}`}
                            >
                              {!locked && <span className="text-gray-300">⠿</span>}
                              {sc.name}
                            </div>
                          ))
                      }
                    </div>
                    {/* Groups */}
                    <div className="flex flex-col gap-3 mb-4">
                      {colGroups.length === 0
                        ? <p className="text-sm text-gray-400 italic">No groups yet — add one below.</p>
                        : colGroups.map(c => {
                            const groupClasses = setup.sportClasses.filter(sc => (c.classIds ?? []).includes(sc.id));
                            const isOver = dndTarget === c.id && dndFromGender === col;
                            return (
                              <div
                                key={c.id}
                                onDragOver={e => { if (dndFromGender === col) { e.preventDefault(); setDndTarget(c.id); } }}
                                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDndTarget(null); }}
                                onDrop={e => {
                                  e.preventDefault();
                                  if (dndClassId && dndFromGender === col) toggleGroupClass(c.id, dndClassId, true);
                                  setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null);
                                }}
                                className={`rounded border-2 p-3 transition-colors ${
                                  isOver ? 'border-secondary bg-blue-50' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <input type="text" value={c.name} disabled={locked}
                                    onChange={e => {
                                      const classes = data!.setup.classes.map(cl => cl.id === c.id ? { ...cl, name: e.target.value } : cl);
                                      persist({ ...data!, setup: { ...data!.setup, classes } });
                                    }}
                                    className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-secondary disabled:bg-transparent disabled:border-transparent"
                                  />
                                  <span className="text-xs text-gray-400 whitespace-nowrap">
                                    {athletes.filter(a =>
                                      (c.classIds ?? []).includes(a.classId) &&
                                      a.gender === (col === 'men' ? 'male' : 'female')
                                    ).length} athletes
                                  </span>
                                  <button onClick={() => deleteMergingGroup(c.id)} disabled={locked}
                                    className="text-red-400 hover:text-red-600 text-xs disabled:opacity-30">✕</button>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-8">
                                  {groupClasses.length === 0
                                    ? <span className="text-xs text-gray-400 italic">← Drop classes here</span>
                                    : groupClasses.map(sc => (
                                        <div
                                          key={sc.id}
                                          draggable={!locked}
                                          onDragStart={() => { setDndClassId(sc.id); setDndFromGroupId(c.id); setDndFromGender(col); }}
                                          onDragEnd={() => { setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null); }}
                                          className={`flex items-center gap-1 px-2.5 py-1 bg-secondary/10 border border-secondary/30 text-secondary rounded text-xs font-semibold select-none transition-opacity ${
                                            locked ? 'cursor-default' : 'cursor-grab'
                                          } ${dndClassId === sc.id && dndFromGender === col ? 'opacity-40' : ''}`}
                                        >
                                          {!locked && <span className="text-secondary/40">⠿</span>}
                                          {sc.name}
                                        </div>
                                      ))
                                  }
                                </div>
                              </div>
                            );
                          })
                      }
                    </div>
                    {!locked && (
                      <div className="flex gap-2">
                        <input type="text" value={colGroupName}
                          onChange={e => setColGroupName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addMergingGroup(col, colGroupName)}
                          placeholder={`Add ${col === 'men' ? "men's" : "women's"} group (e.g. AL, RP3)`}
                          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                        />
                        <button onClick={() => addMergingGroup(col, colGroupName)} disabled={!colGroupName.trim()}
                          className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40">
                          + Add
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Boulder ↔ Merging group assignment matrix */}
            {setup.boulders.length > 0 && setup.classes.length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold text-dark mb-4">Boulder Assignments</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Check the boulders each group will compete on. A boulder can be shared across multiple groups.
                </p>
                <div className="bg-white rounded shadow overflow-x-auto">
                  <table className="text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left font-semibold text-gray-600 min-w-36">Merging Group</th>
                        {setup.boulders.map(b => (
                          <th key={b.id} className="px-3 py-2 text-center font-mono font-semibold text-secondary w-12">
                            B{b.number}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {setup.classes.map(cls => (
                        <tr key={cls.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-dark">{cls.name}</td>
                          {setup.boulders.map(b => {
                            const assigned = cls.boulderIds.includes(b.id);
                            return (
                              <td key={b.id} className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={assigned}
                                  disabled={locked}
                                  onChange={e => toggleBoulderAssignment(cls.id, b.id, e.target.checked)}
                                  className="w-4 h-4 accent-secondary cursor-pointer disabled:cursor-not-allowed"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ ATHLETES TAB ══ */}
        {activeTab === 'athletes' && (
          <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h2 className="text-xl font-bold text-dark">Athletes ({athletes.length})</h2>
            </div>

            {/* Add athlete form */}
            <div className="bg-white rounded shadow p-4 mb-6">
              <h3 className="font-semibold text-dark mb-3 text-sm uppercase tracking-wide">Add Athlete</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Name</label>
                  <input
                    type="text"
                    value={newAthleteName}
                    onChange={e => setNewAthleteName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addAthlete()}
                    placeholder="Full name"
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary w-48"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Gender</label>
                  <select
                    value={newAthleteGender}
                    onChange={e => setNewAthleteGender(e.target.value as 'male' | 'female')}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">{setup.sportClasses.length > 0 ? 'Class' : 'Merging Group'}</label>
                  <select
                    value={newAthleteClassId}
                    onChange={e => setNewAthleteClassId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  >
                    {setup.sportClasses.length > 0 ? (
                      <>
                        <option value="">— select class —</option>
                        {setup.sportClasses.map(sc => (
                          <option key={sc.id} value={sc.id}>{sc.name}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="">— select merging group —</option>
                        {setup.classes.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <button
                  onClick={addAthlete}
                  disabled={!newAthleteName.trim() || !newAthleteClassId}
                  className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40"
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Athletes table */}
            <div className="bg-white rounded shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                    <th className="px-4 py-2 text-left w-8">#</th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-center w-20">Gender</th>
                    <th className="px-4 py-2 text-left w-40">{setup.sportClasses.length > 0 ? 'Class' : 'Merging Group'}</th>
                    <th className="px-4 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {athletes.map((a, i) => (
                    <tr key={a.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={a.name}
                          onChange={e => updateAthleteName(a.id, e.target.value)}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                        />
                      </td>
                      <td className="px-4 py-2 text-center text-gray-500 text-xs">
                        {a.gender === 'male' ? '♂' : '♀'}
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={a.classId}
                          onChange={e => updateAthleteClass(a.id, e.target.value)}
                          className="border border-gray-200 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-2 focus:ring-secondary"
                        >
                          {setup.sportClasses.length > 0 ? (
                            <>
                              <option value="">— no class —</option>
                              {setup.sportClasses.map(sc => (
                                <option key={sc.id} value={sc.id}>{sc.name}</option>
                              ))}
                            </>
                          ) : (
                            <>
                              <option value="">— no merging group —</option>
                              {setup.classes.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </>
                          )}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => deleteAthlete(a.id)}
                          className="text-red-400 hover:text-red-600 text-xs"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                  {athletes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">
                        No athletes yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ JURY TAB ══ */}
        {activeTab === 'jury' && (
          <div>
            {/* Print-only title */}
            <div className="print-only mb-4">
              <h1 className="text-2xl font-bold">Boulder Competition — BK 2026</h1>
              <p className="text-sm text-gray-600 mt-1">Jury Score Sheet · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>

            {setup.boulders.length === 0 || setup.classes.every(c => c.boulderIds.length === 0) ? (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 text-center">
                <p className="text-yellow-800 font-semibold mb-2">Setup not complete</p>
                <p className="text-yellow-700 text-sm">
                  Please add boulders and merging groups in the <strong>Setup</strong> tab first,
                  then assign boulders to groups before entering results.
                </p>
              </div>
            ) : (
              <>
                <div className="no-print flex items-center justify-between mb-4">
                  <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 text-xs text-gray-500 flex gap-4">
                    <span><strong>Z</strong> = Zone (4 pts base)</span>
                    <span><strong>T</strong> = Top (10 pts base)</span>
                    <span>Attempts count · −0.1 per attempt</span>
                  </div>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-gray-700 text-white rounded text-sm font-semibold hover:bg-gray-800 flex items-center gap-2"
                  >
                    🖨 Print / PDF
                  </button>
                </div>
                {/* Scoring legend — visible in print too */}
                <div className="print-only mb-4 text-xs text-gray-500 flex gap-6">
                  <span><strong>Z</strong> = Zone (4 pts − 0.1/attempt)</span>
                  <span><strong>T</strong> = Top (10 pts − 0.1/attempt)</span>
                  <span>Empty = not attempted</span>
                </div>
                {setup.classes.map(cls => {
                  const clsBoulders = cls.boulderIds
                    .map(bid => setup.boulders.find(b => b.id === bid))
                    .filter((b): b is Boulder => !!b);
                  const groupClassIds = cls.classIds ?? [];
                  const clsAthletes = groupClassIds.length > 0
                    ? athletes.filter(a => groupClassIds.includes(a.classId))
                    : athletes.filter(a => a.classId === cls.id);
                  if (clsBoulders.length === 0 || clsAthletes.length === 0) return null;
                  return (
                    <div key={cls.id} className="jury-group mb-8">
                      <div className="bg-secondary text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
                        <h2 className="text-lg font-bold">{cls.name}</h2>
                        <span className="text-blue-200 text-sm">{clsAthletes.length} athletes · {clsBoulders.length} boulders</span>
                      </div>
                      <div className="overflow-x-auto rounded-b shadow">
                        <table className="text-sm border-collapse bg-white w-full">
                          <thead>
                            <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                              <th className="px-4 py-2 text-left sticky left-0 bg-gray-100 z-10 min-w-36">Athlete</th>
                              {clsBoulders.map(b => (
                                <th key={b.id} className="px-2 py-2 text-center font-mono text-secondary w-20">
                                  B{b.number}
                                </th>
                              ))}
                              <th className="px-4 py-2 text-center w-20">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clsAthletes.map(athlete => {
                              const total = clsBoulders.reduce((sum, b) => {
                                const r = getResult(results, athlete.id, b.id);
                                return sum + (r ? scoreBoulder(r) : 0);
                              }, 0);
                              return (
                                <tr key={athlete.id} className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-1 font-semibold text-dark sticky left-0 bg-white z-10 border-r border-gray-100">
                                    {athlete.name}
                                    <span className="ml-1 text-gray-400 font-normal text-xs">
                                      {athlete.gender === 'male' ? '♂' : '♀'}
                                    </span>
                                  </td>
                                  {clsBoulders.map(b => (
                                    <td key={b.id} className="px-1 py-1 text-center border-r border-gray-100">
                                      {/* Screen: interactive entry */}
                                      <div className="no-print">
                                        <JuryCell
                                          result={getResult(results, athlete.id, b.id)}
                                          onChange={patch => updateResult(athlete.id, b.id, patch)}
                                        />
                                      </div>
                                      {/* Print: writable fields */}
                                      <div className="print-only flex flex-col items-center gap-1 py-2 px-1">
                                        <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                          <span className="inline-block border-b border-gray-700 w-8">{'\u00a0'}</span>
                                          <span>att</span>
                                        </div>
                                        <div className="flex gap-3 text-[11px] font-medium text-gray-700">
                                          <span>○Z</span>
                                          <span>○T</span>
                                        </div>
                                      </div>
                                    </td>
                                  ))}
                                  <td className="px-4 py-1 text-center font-bold text-primary text-base">
                                    <span className="no-print">{total > 0 ? total.toFixed(1) : '—'}</span>
                                    <div className="print-only flex flex-col items-center gap-0.5 py-2">
                                      <span className="inline-block border-b border-gray-700 w-14">{'\u00a0'}</span>
                                      <span className="text-[9px] text-gray-500">total</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* ══ RANKINGS TAB ══ */}
        {activeTab === 'rankings' && (
          <div>
            {classRankings.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 text-center">
                <p className="text-yellow-800 font-semibold">No group results yet.</p>
                <p className="text-yellow-700 text-sm mt-1">Add athletes and boulders first.</p>
              </div>
            ) : (
              classRankings.map(({ cls, rows }) => {
                const grpBoulders = cls.boulderIds
                  .map(bid => setup.boulders.find(b => b.id === bid))
                  .filter((b): b is Boulder => !!b);
                return (
                  <div key={cls.id} className="mb-8">
                    <div className="bg-secondary text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
                      <h2 className="text-lg font-bold">{cls.name}</h2>
                      <span className="text-blue-200 text-sm">{rows.length} athletes · {grpBoulders.length} boulders</span>
                    </div>
                    <div className="bg-white rounded-b-lg shadow overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
                            <th className="px-4 py-2 text-center w-12">Rank</th>
                            <th className="px-4 py-2 text-left min-w-36">Athlete</th>
                            <th className="px-3 py-2 text-center w-16">Class</th>
                            {grpBoulders.map(b => (
                              <th key={b.id} className="px-3 py-2 text-center font-mono text-secondary w-16">B{b.number}</th>
                            ))}
                            <th className="px-4 py-2 text-center w-20 font-bold">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => {
                            const isFirst = row.rank === 1 && row.total > 0;
                            return (
                              <tr key={row.athlete.id} className={`border-b border-gray-100 hover:bg-gray-50 ${i === 0 && row.total > 0 ? 'bg-yellow-50' : ''}`}>
                                <td className="px-4 py-2 text-center font-bold text-lg text-secondary">
                                  {row.total === 0 ? '—' : isFirst ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : row.rank}
                                </td>
                                <td className="px-4 py-2 font-semibold text-dark">
                                  {row.athlete.name}
                                  <span className="ml-1.5 text-gray-400 font-normal text-xs">{row.athlete.gender === 'male' ? '♂' : '♀'}</span>
                                </td>
                                <td className="px-3 py-2 text-center text-xs font-semibold text-secondary">
                                  {setup.sportClasses.find(sc => sc.id === row.athlete.classId)?.name || (row.athlete as any).paraclimbingClass || row.athlete.classId}
                                </td>
                                {grpBoulders.map(b => {
                                  const score = row.scores[b.id] ?? 0;
                                  return (
                                    <td key={b.id} className={`px-3 py-2 text-center font-mono font-bold ${
                                      score > 6 ? 'text-green-700 bg-green-50' : score > 0 ? 'text-blue-600 bg-blue-50' : 'text-gray-300'
                                    }`}>
                                      {score > 0 ? formatScore(score) : '—'}
                                    </td>
                                  );
                                })}
                                <td className="px-4 py-2 text-center font-extrabold text-primary text-base">
                                  {row.total > 0 ? row.total.toFixed(1) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

      </div>
    </div>
  );
};

const BoulderAdminPage: React.FC = () => (
  <AdminAccessGate>
    <BoulderAdminInner />
  </AdminAccessGate>
);

export default BoulderAdminPage;
